import { useTranslation } from "react-i18next"

import { AlertTriangle, FileText, Sparkles } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"

import { COMPANY_NAME } from "../../utils/branding"

/** Callout listing sub-fields that came back Not specified (evidence gaps). */
export function DataGapsCallout({ labels }: { labels: string[] }) {
  const { t } = useTranslation()
  if (labels.length === 0) return null
  return (
    <div
      className="border-warning/30 bg-warning/10 text-warning-foreground rounded-lg border p-4"
      data-testid="sa-plan-data-gaps"
      role="status"
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="size-4 shrink-0" />
        {t("segmentAnalysis.plan.dataGaps")}
      </div>
      <p className="mt-1 text-xs">{t("segmentAnalysis.plan.dataGapsHint")}</p>
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {labels.map((label) => (
          <li key={label} className="bg-warning/15 rounded px-2 py-0.5 text-[11px] font-medium">
            {label}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Banner shown right after a plan's first generate — prompts the user to review. */
export function FreshDraftBanner() {
  const { t } = useTranslation()
  return (
    <div
      className="border-primary/30 bg-primary/5 text-foreground flex items-start gap-2 rounded-lg border p-4"
      data-testid="sa-plan-fresh-draft"
    >
      <Sparkles className="text-primary mt-0.5 size-4 shrink-0" />
      <div>
        <p className="text-sm font-semibold">{t("segmentAnalysis.plan.freshDraftTitle")}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {t("segmentAnalysis.plan.freshDraftBody")}
        </p>
      </div>
    </div>
  )
}

/** Banner when a plan was generated but most sub-fields are Not specified. */
export function ThinEvidenceBanner({
  canEdit,
  isGenerating,
  onRegenerate,
}: {
  canEdit: boolean
  isGenerating: boolean
  onRegenerate: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="border-warning/30 bg-warning/10 text-warning-foreground flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
      data-testid="sa-plan-thin-evidence"
      role="status"
    >
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="size-4 shrink-0" />
          {t("segmentAnalysis.plan.mostlyNsTitle")}
        </p>
        <p className="mt-1 text-xs">{t("segmentAnalysis.plan.mostlyNsBody")}</p>
      </div>
      {canEdit ? (
        <Button
          data-testid="sa-plan-thin-evidence-regenerate"
          disabled={isGenerating}
          size="sm"
          variant="outline"
          onClick={onRegenerate}
        >
          <Sparkles className="size-4" />
          {isGenerating
            ? t("segmentAnalysis.plan.generating")
            : t("segmentAnalysis.plan.regenerate")}
        </Button>
      ) : null}
    </div>
  )
}

/** Empty state before the first generate (not used for thin-evidence drafts). */
export function PlanEmptyState({
  kind,
  canEdit,
  isGenerating,
  onGenerate,
}: {
  kind: "ccp" | "competitor"
  canEdit: boolean
  isGenerating: boolean
  onGenerate: () => void
}) {
  const { t } = useTranslation()
  const title =
    kind === "ccp"
      ? t("segmentAnalysis.plan.emptyCcpTitle")
      : t("segmentAnalysis.plan.emptyCompetitorTitle")
  const body =
    kind === "ccp"
      ? t("segmentAnalysis.plan.emptyCcpBody")
      : t("segmentAnalysis.plan.emptyCompetitorBody", { organization: COMPANY_NAME })
  return (
    <EmptyState
      className="border-border bg-card rounded-xl border border-dashed p-12"
      description={body}
      icon={FileText}
      title={title}
      primaryAction={
        canEdit ? (
          <Button data-testid="sa-plan-generate" disabled={isGenerating} onClick={onGenerate}>
            <Sparkles className="size-4" />
            {isGenerating
              ? t("segmentAnalysis.plan.generating")
              : t("segmentAnalysis.plan.generate")}
          </Button>
        ) : undefined
      }
    />
  )
}
